import React, { useState, useEffect } from 'react';
import { db, auth } from '../../firebase';
import { ref, onValue, update } from 'firebase/database';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaFilePdf, FaExternalLinkAlt, FaCheck, FaTimes, FaUserTie, FaSync, FaSearch, FaChevronRight } from 'react-icons/fa';

const DOCUMENT_LABELS = {
    '1': "शासनमान्य संस्थेचे प्रमाणपत्र",
    '2': "शेवटच्या वर्षाचे गुणपत्रक",
    '3': "रहिवासी (Domicile) प्रमाणपत्र",
    '4': "साक्षांकित फोटो व सही",
    '5': "आधारकार्ड",
    '6': "पॅनकार्ड",
    '7': "नोकरीबाबत प्रतिज्ञापत्र",
    'aadharFile': "आधारकार्ड",
    'panFile': "पॅनकार्ड",
    'gstFile': "जी.एस.टी प्रमाणपत्र",
    'regCertificateFile': "नोंदणी प्रमाणपत्र"
};

const DocumentVerification = () => {
    const location = useLocation();
    const passedUser = location.state?.selectedUser;

    const [registrations, setRegistrations] = useState(passedUser ? [passedUser] : []);
    const [selectedUser, setSelectedUser] = useState(passedUser || null);
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (!user) navigate('/login');
        });

        if (passedUser) {
            const entityType = location.state?.entityType;
            let basePath = '';
            if (entityType === 'society') {
                basePath = passedUser.type === 'नवीन' ? 'ekamwatapzp/SocietyLotteryRegistrations' : 'ekamwatapzp/SocietyRegistrations';
            } else {
                basePath = passedUser.type === 'नवीन' ? 'ekamwatapzp/EngineerLotteryRegistrations' : 'ekamwatapzp/EngineerRegistrations';
            }
            
            const userRef = ref(db, `${basePath}/${passedUser.id}`);
            
            const unsubData = onValue(userRef, (snapshot) => {
                if (snapshot.exists()) {
                    const data = { id: snapshot.key, type: passedUser.type, ...snapshot.val() };
                    setSelectedUser(data);
                    setRegistrations([data]);
                }
                setLoading(false);
            });
            return () => {
                unsubscribe();
                unsubData();
            };
        } else {
            const regRef = ref(db, 'ekamwatapzp/EngineerRegistrations');
            const unsubData = onValue(regRef, (snapshot) => {
                if (snapshot.exists()) {
                    const data = snapshot.val();
                    const list = Object.keys(data)
                        .map(key => ({
                            id: key,
                            ...data[key]
                        }))
                        .filter(user => user.documents); // Only show users with documents
                    setRegistrations(list.reverse());
                    
                    if (selectedUser && !passedUser) {
                        const updatedUser = list.find(u => u.id === selectedUser.id);
                        if (updatedUser) setSelectedUser(updatedUser);
                    }
                } else {
                    setRegistrations([]);
                }
                setLoading(false);
            });

            return () => {
                unsubscribe();
                unsubData();
            };
        }
    }, [navigate, passedUser?.id, selectedUser?.id]);

    const handleVerifyValue = async (userId, docId, status) => {
        setUpdating(docId);
        try {
            const isLottery = selectedUser?.type === 'नवीन';
            const entityType = location.state?.entityType;
            let basePath = '';
            if (entityType === 'society') {
                basePath = isLottery ? 'ekamwatapzp/SocietyLotteryRegistrations' : 'ekamwatapzp/SocietyRegistrations';
            } else {
                basePath = isLottery ? 'ekamwatapzp/EngineerLotteryRegistrations' : 'ekamwatapzp/EngineerRegistrations';
            }
            const itemRef = ref(db, `${basePath}/${userId}/documents/${docId}`);
            await update(itemRef, { approvedByAdmin: status === 'approved' });
        } catch (error) {
            console.error("Error updating status:", error);
            alert("Error updating status: " + error.message);
        } finally {
            setUpdating(null);
        }
    };

    const getStatusStyle = (approvedByAdmin) => {
        if (approvedByAdmin === true) return 'bg-emerald-100 text-emerald-700 border-emerald-200';
        if (approvedByAdmin === false) return 'bg-rose-100 text-rose-700 border-rose-200';
        return 'bg-amber-100 text-amber-700 border-amber-200';
    };

    const filteredRegistrations = registrations.filter(user => {
        const fullName = (user.societyName || `${user.firstName || ''} ${user.surname || ''}`).toLowerCase();
        return fullName.includes(searchTerm.toLowerCase()) || (user.whatsappNo && user.whatsappNo.includes(searchTerm));
    });

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center">
                <div className="flex flex-col items-center gap-4">
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-200 border-t-blue-600"></div>
                    <p className="text-slate-500 font-medium">लोड होत आहे...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex-1 flex flex-col h-screen overflow-hidden">
                {/* Header */}
                <header className="bg-white border-b border-slate-200 p-4 md:p-6 shrink-0">
                    <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                            <div className="p-3 bg-blue-600 rounded-xl text-white shadow-lg shadow-blue-200">
                                <FaUserTie className="text-2xl" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-slate-800">सर्व कागदपत्रांची पडताळणी</h1>
                                <p className="text-slate-500 text-sm">सबमिट केलेल्या सर्व अभियंत्यांची यादी</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="relative">
                                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                <input 
                                    type="text" 
                                    placeholder="नाव किंवा मोबाईल शोधा..." 
                                    className="pl-10 pr-4 py-2 bg-slate-100 border-transparent focus:bg-white focus:border-blue-500 rounded-xl text-sm w-full md:w-64 transition-all outline-none"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                            <button 
                                onClick={() => setLoading(true)}
                                className="p-2.5 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-xl transition-colors"
                                title="रिफ्रेश करा"
                            >
                                <FaSync className={loading ? 'animate-spin' : ''} />
                            </button>
                        </div>
                    </div>
                </header>

                <main className="flex-1 overflow-hidden flex flex-col md:flex-row">
                    {/* User List Sidebar */}
                    <div className="w-full md:w-80 border-r border-slate-200 bg-white overflow-y-auto shrink-0">
                        <div className="p-4 border-b border-slate-100 bg-slate-50/50">
                            <h2 className="text-xs font-bold text-slate-400 uppercase tracking-wider">अभियंत्यांची यादी ({filteredRegistrations.length})</h2>
                        </div>
                        <div className="divide-y divide-slate-50">
                            {filteredRegistrations.map(user => (
                                <button
                                    key={user.id}
                                    onClick={() => setSelectedUser(user)}
                                    className={`w-full text-left p-4 hover:bg-blue-50/50 transition-colors flex items-center justify-between group ${selectedUser?.id === user.id ? 'bg-blue-50 border-l-4 border-blue-600' : 'border-l-4 border-transparent'}`}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-white shadow-sm ${selectedUser?.id === user.id ? 'bg-blue-600' : 'bg-slate-400 group-hover:bg-blue-500'}`}>
                                            {(user.societyName || user.firstName || '?').charAt(0)}
                                        </div>
                                        <div>
                                            <p className={`font-bold text-sm ${selectedUser?.id === user.id ? 'text-blue-700' : 'text-slate-700'}`}>
                                                {user.societyName || `${user.firstName || ''} ${user.surname || ''}`.trim()}
                                            </p>
                                            <p className="text-xs text-slate-400">{user.whatsappNo || 'क्रमांक नाही'}</p>
                                        </div>
                                    </div>
                                    <FaChevronRight className={`text-xs transition-transform ${selectedUser?.id === user.id ? 'text-blue-600 translate-x-1' : 'text-slate-300'}`} />
                                </button>
                            ))}
                            {filteredRegistrations.length === 0 && (
                                <div className="p-8 text-center text-slate-400 text-sm">
                                    काहीही आढळले नाही
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Document View Area */}
                    <div className="flex-1 overflow-y-auto bg-slate-50/50 p-4 md:p-8">
                        {selectedUser ? (
                            <div className="max-w-4xl mx-auto space-y-6">
                                {/* Selected User Info */}
                                <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
                                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                        <div className="flex items-center gap-4">
                                            <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-2xl flex items-center justify-center text-2xl font-bold">
                                                {(selectedUser.societyName || selectedUser.firstName || '?').charAt(0)}
                                            </div>
                                            <div>
                                                <h2 className="text-xl font-bold text-slate-800">{selectedUser.societyName || `${selectedUser.firstName || ''} ${selectedUser.surname || ''}`.trim()}</h2>
                                                <p className="text-slate-500 text-sm">{selectedUser.email} | {selectedUser.whatsappNo}</p>
                                            </div>
                                        </div>
                                        <div className="px-4 py-2 bg-slate-100 rounded-xl text-xs font-bold text-slate-500">
                                            ID: {selectedUser.id}
                                        </div>
                                    </div>
                                </div>

                                {/* Documents List */}
                                <div className="space-y-4">
                                    <h3 className="text-lg font-bold text-slate-700 flex items-center gap-2">
                                        <FaFilePdf className="text-blue-600" /> अपलोड केलेली कागदपत्रे
                                    </h3>
                                    {Object.entries(selectedUser.documents || {}).map(([docId, doc]) => (
                                        <div key={docId} className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
                                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                                                <div className="flex items-start gap-4 flex-1">
                                                    <div className="p-4 bg-blue-50 rounded-2xl text-blue-600 shrink-0">
                                                        <FaFilePdf size={32} />
                                                    </div>
                                                    <div className="space-y-1 overflow-hidden">
                                                        <h3 className="font-bold text-slate-800 text-lg uppercase tracking-tight truncate">
                                                            {DOCUMENT_LABELS[docId] || docId.replace(/([A-Z])/g, ' $1').trim()}
                                                        </h3>
                                                        <div className="flex flex-wrap items-center gap-3">
                                                            <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold border ${getStatusStyle(doc.approvedByAdmin)}`}>
                                                                {doc.approvedByAdmin === true ? 'approved' : doc.approvedByAdmin === false ? 'rejected' : 'pending'}
                                                            </span>
                                                            {doc.url && (
                                                                <a 
                                                                    href={doc.url} 
                                                                    target="_blank" 
                                                                    rel="noopener noreferrer"
                                                                    className="inline-flex items-center gap-2 text-blue-600 hover:text-white bg-blue-50 hover:bg-blue-600 border-2 border-blue-600 font-bold text-sm px-4 py-1.5 rounded-xl transition-all shadow-sm"
                                                                >
                                                                    कागदपत्र पहा <FaExternalLinkAlt size={12} />
                                                                </a>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="flex items-center gap-3 bg-slate-50 p-3 rounded-2xl shrink-0">
                                                    <button
                                                        disabled={updating === docId || doc.approvedByAdmin === true}
                                                        onClick={() => handleVerifyValue(selectedUser.id, docId, 'approved')}
                                                        className={`flex-1 md:flex-none inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl font-bold text-sm transition-all ${
                                                            doc.approvedByAdmin === true 
                                                            ? 'bg-emerald-600 text-white cursor-default' 
                                                            : 'bg-white text-emerald-600 hover:bg-emerald-600 hover:text-white border-2 border-emerald-600 shadow-sm'
                                                        } disabled:opacity-50`}
                                                    >
                                                        <FaCheck /> {doc.approvedByAdmin === true ? 'मंजूर' : 'मंजूर करा'}
                                                    </button>
                                                    <button
                                                        disabled={updating === docId || doc.approvedByAdmin === false}
                                                        onClick={() => handleVerifyValue(selectedUser.id, docId, 'rejected')}
                                                        className={`flex-1 md:flex-none inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl font-bold text-sm transition-all ${
                                                            doc.approvedByAdmin === false 
                                                            ? 'bg-rose-600 text-white cursor-default' 
                                                            : 'bg-white text-rose-600 hover:bg-rose-600 hover:text-white border-2 border-rose-600 shadow-sm'
                                                        } disabled:opacity-50`}
                                                    >
                                                        <FaTimes /> {doc.approvedByAdmin === false ? 'नामंजूर' : 'नामंजूर करा'}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <div className="h-full flex flex-col items-center justify-center text-center p-8">
                                <div className="w-24 h-24 bg-white rounded-3xl shadow-xl shadow-slate-200/50 flex items-center justify-center mb-6 animate-bounce">
                                    <FaUserTie className="text-4xl text-blue-600" />
                                </div>
                                <h3 className="text-2xl font-bold text-slate-800 mb-2">अभियंता निवडा</h3>
                                <p className="text-slate-500 max-w-xs mx-auto">
                                    कागदपत्रे पाहण्यासाठी आणि पडताळणी करण्यासाठी डावीकडील यादीतून एक अभियंता निवडा.
                                </p>
                            </div>
                        )}
                    </div>
                </main>
        </div>
    );
};

export default DocumentVerification;
